// Tiny count-up driven by Motion. Used by the operator and
// data-confident variants to make freshly-detected file counts feel
// alive. Honors prefers-reduced-motion by jumping straight to the
// final value.

import {
	animate,
	type MotionValue,
	motion,
	useMotionValue,
	useReducedMotion,
	useTransform,
} from "motion/react";
import { useEffect } from "react";
import { EASE_OUT_EXPO } from "@/lib/motion";

interface Props {
	value: number;
	durationMs?: number;
	className?: string;
	format?: (n: number) => string;
}

export function AnimatedCounter({
	value,
	durationMs = 700,
	className,
	format = (n) => Math.round(n).toLocaleString(),
}: Props) {
	const reduce = useReducedMotion();
	const mv: MotionValue<number> = useMotionValue(0);
	const display = useTransform(mv, (n) => format(n));

	useEffect(() => {
		if (reduce) {
			mv.set(value);
			return;
		}
		const controls = animate(mv, value, {
			duration: durationMs / 1000,
			ease: EASE_OUT_EXPO,
		});
		return () => controls.stop();
	}, [value, durationMs, reduce, mv]);

	return <motion.span className={className}>{display}</motion.span>;
}
